<template>
    <div class="mte-page">
        <!-- Header -->
        <div class="mte-header">
            <div class="mte-header-left">
                <button class="mte-back" @click="cancelEdit"><i class="fas fa-arrow-left"></i></button>
                <div>
                    <h1 class="mte-title">{{ editMode ? "Update Template" : "Create Template" }}</h1>
                    <p class="mte-subtitle">{{ editMode ? "Modify your document template" : "Build a reusable document template with dynamic tags" }}</p>
                </div>
            </div>
            <div class="mte-header-actions">
                <button class="mte-btn mte-btn--ghost" @click="cancelEdit">
                    <i class="fas fa-times"></i> Cancel
                </button>
                <button class="mte-btn mte-btn--primary" @click="createOrUpdateMasterTemplate" :disabled="processing">
                    <i class="fas fa-spinner fa-spin" v-if="processing"></i>
                    <i class="fas fa-save" v-else></i>
                    {{ processing ? "Saving..." : editMode ? "Update Template" : "Create Template" }}
                </button>
            </div>
        </div>

        <!-- Main Layout -->
        <div class="mte-layout">
            <!-- Editor Area -->
            <div class="mte-editor-card">
                <div class="mte-card__header">
                    <i class="fas fa-file-alt"></i> Template Content
                </div>
                <div class="mte-card__body">
                    <div class="mte-field-title">
                        <CustomInput v-model="title" label="Template Title*" />
                    </div>
                    <div class="mte-editor-wrap">
                        <quill-editor
                            ref="myQuillEditor"
                            v-model="content"
                            :options="editorOptions"
                        />
                    </div>
                </div>
            </div>

            <!-- Tags Sidebar -->
            <div class="mte-tags-card">
                <div class="mte-card__header">
                    <i class="fas fa-tags"></i> Dynamic Tags
                </div>
                <p class="mte-tags-hint">Click a tag to insert it at cursor position in the editor</p>
                <div class="mte-tags-list">
                    <div
                        v-for="(group, gIdx) in tagGroups"
                        :key="gIdx"
                        class="mte-tag-group"
                    >
                        <div class="mte-tag-group__label">{{ group.label }}</div>
                        <div class="mte-tag-group__items">
                            <button
                                v-for="(tag, tIdx) in group.tags"
                                :key="tIdx"
                                class="mte-tag-btn"
                                @click="insertTag(tag.value)"
                                :title="tag.value"
                            >
                                <i :class="tag.icon || 'fas fa-code'"></i>
                                {{ tag.label }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { quillEditor } from "vue-quill-editor"
import "quill/dist/quill.core.css"
import "quill/dist/quill.snow.css"
import Button from "../../components/UI/Button.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import { http } from "@/utils/http"
import PageCard from "../../components/layout/PageCard.vue"

export default {
    data() {
        return {
            title: "",
            content: "",
            processing: false,
            editMode: true,
            editTemplateData: null,
            editorOptions: {
                theme: "snow",
                placeholder: "Start writing your template content here...",
                modules: {
                    toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        [{ header: 1 }, { header: 2 }],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ indent: "-1" }, { indent: "+1" }],
                        [{ direction: "rtl" }],
                        [{ size: ["small", false, "large", "huge"] }],
                        [{ color: [] }, { background: [] }],
                        [{ font: [] }],
                        [{ align: [] }],
                        ["clean"],
                    ],
                },
            },
            tagGroups: [
                {
                    label: "Personal",
                    tags: [
                        { label: "Full Name", value: "[Full Name]", icon: "fas fa-user" },
                        { label: "First Name", value: "[First Name]", icon: "fas fa-user" },
                        { label: "Last Name", value: "[Last Name]", icon: "fas fa-user" },
                        { label: "Date of Birth", value: "[DOB]", icon: "fas fa-birthday-cake" },
                        { label: "Email", value: "[Email]", icon: "fas fa-envelope" },
                        { label: "Mobile", value: "[Mobile]", icon: "fas fa-phone" },
                        { label: "Employee Code", value: "[Employee Code]", icon: "fas fa-id-badge" },
                    ],
                },
                {
                    label: "Address",
                    tags: [
                        { label: "Country", value: "[Country]", icon: "fas fa-globe" },
                        { label: "State", value: "[State]", icon: "fas fa-map" },
                        { label: "City", value: "[City]", icon: "fas fa-city" },
                        { label: "Address", value: "[Address]", icon: "fas fa-map-marker-alt" },
                    ],
                },
                {
                    label: "Employment",
                    tags: [
                        { label: "Department", value: "[Department]", icon: "fas fa-building" },
                        { label: "Designation", value: "[Designation]", icon: "fas fa-briefcase" },
                        { label: "Supervisor", value: "[Supervisor/Reporter]", icon: "fas fa-user-tie" },
                        { label: "Employment Status", value: "[Employment Status]", icon: "fas fa-check-circle" },
                        { label: "Work Location", value: "[Work Location]", icon: "fas fa-map-pin" },
                        { label: "Salary Company", value: "[Salary Company]", icon: "fas fa-building" },
                    ],
                },
                {
                    label: "Compensation",
                    tags: [
                        { label: "CTC", value: "[CTC]", icon: "fas fa-rupee-sign" },
                        { label: "Salary Total", value: "[Salary Total]", icon: "fas fa-money-bill-wave" },
                    ],
                },
                {
                    label: "Termination / Assets",
                    tags: [
                        { label: "Term. Start Date", value: "[Termination Start Date]", icon: "fas fa-calendar" },
                        { label: "Term. End Date", value: "[Termination End Date]", icon: "fas fa-calendar-check" },
                        { label: "Notice Period Days", value: "[Notice Period Days]", icon: "fas fa-hourglass-half" },
                        { label: "Asset Name", value: "[Asset Name]", icon: "fas fa-box" },
                        { label: "Asset Price", value: "[Asset Price]", icon: "fas fa-tag" },
                        { label: "Serial Number", value: "[Serial Number]", icon: "fas fa-barcode" },
                    ],
                },
            ],
        }
    },
    components: {
        Button,
        CustomInput,
        quillEditor,
        PageCard,
    },
    async mounted() {
        await this.initIfEditMode()
    },
    methods: {
        insertTag(tag) {
            const quill = this.$refs.myQuillEditor.quill
            const range = quill.getSelection(true)
            if (range) {
                quill.insertText(range.index, tag)
                quill.focus()
            }
        },
        cancelEdit() {
            this.$router.push("/master-templates")
        },
        async initIfEditMode() {
            const id = this.$route.params.id
            if (id) {
                this.editMode = true
                const res = await http.get("/master-template/" + id)
                this.editTemplateData = res.data.result
                if (this.editTemplateData) {
                    this.title = this.editTemplateData.title || ""
                    this.content = this.editTemplateData.content || ""
                }
            } else {
                this.editMode = false
            }
        },
        async createOrUpdateMasterTemplate() {
            if (this.processing) return
            if (!this.title) {
                return this.$toast.error("Please enter a template title")
            }
            this.processing = true

            const formDataObj = {
                title: this.title,
                content: this.content,
            }

            try {
                if (this.editMode) {
                    await http.patch(`/master-template/${this.editTemplateData.id}`, formDataObj)
                    this.$toast.success("Master Template Updated Successfully")
                } else {
                    await http.post("/master-template", formDataObj)
                    this.$toast.success("Master Template Created Successfully")
                }
                window.location.href = "/master-templates"
            } catch (err) {
                console.log("error in creating/updating Master Template", err)
                this.$toast.error(
                    (err.response && err.response.data && err.response.data.message) ||
                        "Some error occurred"
                )
            }
            this.processing = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.mte-page {
    animation: mteFade .25s ease;
}
@keyframes mteFade {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ── Header ── */
.mte-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
}
.mte-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}
.mte-back {
    width: 36px;
    height: 36px;
    border-radius: $radiusSm;
    border: 1px solid $gray200;
    background: white;
    color: $gray600;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    transition: all $transitionFast;

    &:hover { background: $gray50; border-color: $gray300; color: $gray800; }
}
.mte-title {
    font-size: 20px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}
.mte-subtitle {
    font-size: 13px;
    color: $gray400;
    margin: 2px 0 0;
}
.mte-header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

/* ── Layout ── */
.mte-layout {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 20px;
    align-items: start;
}

/* ── Cards ── */
.mte-editor-card, .mte-tags-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    box-shadow: $shadowSm;
    overflow: hidden;
}
.mte-card__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 13px 18px;
    border-bottom: 1px solid $gray100;
    background: $gray50;
    font-size: 13px;
    font-weight: 700;
    color: $gray800;

    i { color: $primaryColor; font-size: 13px; }
}
.mte-card__body {
    padding: 18px;
}
.mte-field-title {
    margin-bottom: 14px;
}

/* ── Editor ── */
.mte-editor-wrap {
    border: 1px solid $gray200;
    border-radius: $radiusSm;
    overflow: hidden;
}
</style>

<style lang="scss">
@import "../../assets/variables.scss";

/* Quill overrides (unscoped) */
.mte-editor-wrap {
    .ql-toolbar {
        border: none !important;
        border-bottom: 1px solid $gray200 !important;
        background: $gray50 !important;
        padding: 8px 10px !important;
    }
    .ql-container {
        border: none !important;
        font-size: 14px;
        font-family: inherit;
        min-height: 320px;
    }
    .ql-editor {
        min-height: 320px;
        padding: 16px;
        line-height: 1.65;
        color: $gray700;

        &.ql-blank::before {
            color: $gray400;
            font-style: normal;
        }
    }
}

/* ── Tags Sidebar ── */
.mte-tags-hint {
    font-size: 11px;
    color: $gray400;
    padding: 10px 16px 0;
    margin: 0;
}
.mte-tags-list {
    padding: 10px 14px 14px;
    max-height: 600px;
    overflow-y: auto;
}
.mte-tag-group {
    margin-bottom: 12px;

    &:last-child { margin-bottom: 0; }
}
.mte-tag-group__label {
    font-size: 10px;
    font-weight: 700;
    color: $gray400;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 6px;
    padding-left: 2px;
}
.mte-tag-group__items {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}
.mte-tag-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border: 1px solid $gray200;
    border-radius: 20px;
    background: white;
    color: $gray600;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: all $transitionFast;
    white-space: nowrap;

    i { font-size: 9px; color: $gray400; }

    &:hover {
        background: $primaryLight;
        border-color: lighten($primaryColor, 30%);
        color: $primaryColor;

        i { color: $primaryColor; }
    }
}

/* ── Buttons ── */
.mte-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 20px;
    border-radius: $radiusSm;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    transition: all $transitionFast;

    &:disabled { opacity: 0.55; cursor: not-allowed; }

    &--primary {
        background: $primaryColor;
        color: white;
        &:hover:not(:disabled) { background: $primaryDark; }
    }
    &--ghost {
        background: white;
        color: $gray600;
        border: 1px solid $gray200;
        &:hover { background: $gray50; color: $gray800; }
    }
}

/* ── Responsive ── */
@media (max-width: 900px) {
    .mte-layout { grid-template-columns: 1fr; }
    .mte-tags-list { max-height: 250px; }
}
@media (max-width: 640px) {
    .mte-header { flex-direction: column; align-items: flex-start; }
    .mte-header-actions { width: 100%; }
    .mte-btn { flex: 1; justify-content: center; }
}
</style>
